import React from 'react';
import type { BrainAtlasType, MultiLang } from '../../../config';
import Functions from '../../../functions';
import ItemUtil, { type Item, type ItemBinder } from '../../lib/ItemUtil';
import ItemType from '..';
import DetailBase from '../lib/DetailBase';
import ItemTypeField from '../lib/field';

interface Props {
    lang: MultiLang;
    item: ItemBinder;
    type: BrainAtlasType;
}

const BinderLinkItems: React.FC<Props> = (props) => {
    const { lang, item, type } = props;
    const [items, setItems] = React.useState<Item[]>([]);

    const isMounted = React.useRef<boolean>(false);
    React.useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    React.useEffect(() => {
        const itemIds = item.item_link;
        ItemUtil.getList(type, itemIds, (results) => {
            if (isMounted.current) {
                setItems(results.data);
            }
        });
    }, [item.item_link, type]);

    return (
        <table className="listTable">
            <tbody>
                {items.map((item, idx) => {
                    const evenodd = idx % 2 ? 'even' : 'odd';
                    return (
                        <tr key={item.item_id}>
                            <td className={evenodd}>
                                <ItemType.List lang={lang} item={item} type={type} />
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

class BinderDetail extends DetailBase {
    getFields() {
        const { lang, type } = this.props;
        const item = this.props.item as ItemBinder;
        return [
            { label: 'ID', value: item.doi },
            { label: '[en]Title[/en][ja]タイトル[/ja]', value: Functions.mlang(item.title, lang) },
            {
                label: '[en]Free Keywords[/en][ja]フリーキーワード[/ja]',
                value: <ItemTypeField.FreeKeyword lang={lang} keyword={item.keyword} />,
            },
            {
                label: '[en]Description[/en][ja]概要[/ja]',
                value: <ItemTypeField.Description lang={lang} description={item.description} />,
            },
            {
                label: '[en]Last Modified Date[/en][ja]最終更新日[/ja]',
                value: <ItemTypeField.DateTime lang={lang} date={item.last_update_date} />,
            },
            {
                label: '[en]Created Date[/en][ja]作成日[/ja]',
                value: <ItemTypeField.DateTime lang={lang} date={item.creation_date} />,
            },
            {
                label: '[en]Contributor[/en][ja]登録者[/ja]',
                value: <ItemTypeField.Contributor lang={lang} uname={item.uname} name={item.name} />,
            },
            { label: '[en]Item Type[/en][ja]アイテムタイプ[/ja]', value: item.item_type_display_name },
            {
                label: '[en]Change Log(History)[/en][ja]変更履歴[/ja]',
                value: <ItemTypeField.ChangeLog lang={lang} changelog={item.changelog} />,
            },
            {
                label: 'Index',
                value: <ItemTypeField.ItemIndex lang={lang} index={item.index} type={type} />,
            },
        ];
    }

    render() {
        const { lang, type } = this.props;
        const item = this.props.item as ItemBinder;
        const detail = super.render.call(this);
        return (
            <>
                {detail}
                <h4>Registered Items</h4>
                <BinderLinkItems lang={lang} item={item} type={type} />
            </>
        );
    }
}

export default BinderDetail;
